import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Sparkles, Lock, ChevronRight, Loader2, ScrollText } from "lucide-react";

export default function Profile() {
  const { t, pick, user } = useApp();
  const navigate = useNavigate();
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/palm/readings")
      .then(({ data }) => setReadings(data))
      .catch((e) => { if (e?.response?.status === 401) navigate("/login"); })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  return (
    <div className="mx-auto max-w-4xl px-6 py-14">
      <div className="flex items-center gap-4 mb-10 rise-in">
        {user?.picture ? (
          <img src={user.picture} alt={user.name} className="h-16 w-16 rounded-full ring-2 ring-amber-400/50" />
        ) : (
          <div className="h-16 w-16 rounded-full bg-purple-700 grid place-items-center font-serif text-2xl">{user?.name?.[0]}</div>
        )}
        <div>
          <h1 className="font-serif text-3xl font-bold text-amber-50">{t.profile.title}</h1>
          <p className="text-slate-400 text-sm">{user?.name} · {t.profile.subtitle}</p>
        </div>
      </div>

      {loading ? (
        <div className="grid place-items-center py-20 text-amber-200"><Loader2 className="animate-spin" /></div>
      ) : readings.length === 0 ? (
        <div className="glass rounded-3xl p-14 text-center" data-testid="profile-empty">
          <ScrollText className="mx-auto text-amber-300/70 mb-4" size={40} />
          <p className="text-slate-300 mb-6">{t.profile.empty}</p>
          <button onClick={() => navigate("/upload")} className="btn-gold rounded-full px-8 py-3 inline-flex items-center gap-2">
            <Sparkles size={18} /> {t.profile.readNow}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {readings.map((r) => (
            <Link
              key={r.reading_id}
              to={`/result/${r.reading_id}`}
              data-testid={`reading-item-${r.reading_id}`}
              className="glass rounded-2xl p-5 flex items-center gap-4 hover:scale-[1.01] transition-transform"
            >
              <div className="h-12 w-12 rounded-xl grid place-items-center bg-gradient-to-br from-amber-500/20 to-purple-600/20 border border-amber-500/30 shrink-0">
                <Sparkles className="text-amber-300" size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-200 truncate">{pick(r.data.overview)}</p>
                <div className="mt-1 flex items-center gap-3 text-xs">
                  <span className="text-slate-500">{new Date(r.created_at).toLocaleDateString()}</span>
                  <span className={`inline-flex items-center gap-1 ${r.unlocked ? "text-emerald-300" : "text-amber-300/70"}`}>
                    {r.unlocked ? <Sparkles size={12} /> : <Lock size={12} />}
                    {r.unlocked ? t.profile.unlocked : t.profile.preview}
                  </span>
                </div>
              </div>
              <ChevronRight className="text-slate-500 shrink-0" size={20} />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
